import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertTriangle, XCircle, Info, Printer, RotateCcw, Crown } from 'lucide-react';
import QrScanner from '../../components/QrScanner';
import EventPicker from '../../components/EventPicker';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import { post } from '../../api/client';
import { Card, Button, Badge, SectionTitle, EmptyState } from '../../components/UI';
import { fmtTime } from '../../utils/format';

/**
 * The staff scanning desk — the same five modes as the staff app, for a laptop
 * with a webcam or a phone browser. The mode decides where a scanned pass goes.
 */
const ALL_MODES = [
  { value: 'event_entry', label: 'Event entry' },
  { value: 'session', label: 'Session' },
  { value: 'bus_boarding', label: 'Bus boarding', module: 'buses' },
  { value: 'luggage', label: 'Find a bag', luggage: true },
  { value: 'luggage_tag', label: 'Tag a bag', luggage: true },
];

/** Turns an API response into what the result card shows. */
function toResult(mode, res) {
  const d = res.data || {};
  if (mode === 'luggage_tag') {
    return {
      tone: 'success', title: 'Bag tagged', name: d.guest?.name, isVip: d.guest?.isVip,
      message: res.message, big: d.tagNumber,
      meta: d.room ? `Room ${d.room}` : 'Room not allocated yet',
      counts: d.bagsForGuest ? `${d.bagsForGuest} bag${d.bagsForGuest === 1 ? '' : 's'} for this guest` : null,
      qrImage: d.qrImage, tagNumber: d.tagNumber,
    };
  }
  if (mode === 'luggage') {
    if (d.mode === 'owner') {
      return {
        tone: 'info', title: 'Guest bags', name: d.guest?.name, isVip: d.guest?.isVip,
        message: res.message, big: `${d.bags?.length || 0} bag${d.bags?.length === 1 ? '' : 's'}`,
      };
    }
    return {
      tone: 'success', title: 'Bag found', name: d.bag?.user?.name, message: res.message,
      big: d.bag?.tagNumber, meta: d.bag?.room?.roomNumber ? `Room ${d.bag.room.roomNumber}` : null,
    };
  }
  if (mode === 'bus_boarding') {
    return {
      tone: d.alreadyBoarded ? 'warning' : 'success',
      title: d.alreadyBoarded ? 'Already boarded' : 'Boarded',
      name: d.guest?.name, isVip: d.guest?.isVip, message: res.message,
      big: d.seatNumber ? `Seat ${d.seatNumber}` : null,
      meta: d.bus ? `Bus ${d.bus.busNumber}` : null,
      counts: d.boarded !== undefined ? `${d.boarded} boarded · ${d.pending} pending` : null,
    };
  }
  const party = d.companions || 0;
  return {
    tone: d.duplicate ? 'warning' : d.partyState === 'up' ? 'warning' : 'success',
    title: d.duplicate ? 'Already scanned' : 'Marked present',
    name: d.guest?.name, isVip: d.guest?.isVip, message: res.message,
    meta: d.guest?.designation || d.guest?.employeeCode, big: d.session || null,
    counts: party
      ? `${party + 1} people in — ${party} with them${d.partyState === 'up' ? ', more than the sheet said' : ''}`
      : null,
  };
}

const TONE = {
  success: { color: 'var(--ok)', bg: 'var(--ok-dim)', Icon: CheckCircle2 },
  warning: { color: 'var(--warn)', bg: 'var(--warn-dim)', Icon: AlertTriangle },
  error: { color: 'var(--danger)', bg: 'var(--danger-dim)', Icon: XCircle },
  info: { color: 'var(--info)', bg: 'var(--info-dim)', Icon: Info },
};

export default function ScanDesk() {
  const { event, eventId, hasModule } = useEvents();
  const toast = useToast();

  const [mode, setMode] = useState('event_entry');
  const [sessionId, setSessionId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [feed, setFeed] = useState([]);
  // Set when the event counts guests and this badge has none on record yet.
  const [asking, setAsking] = useState(null);

  const handlesLuggage = hasModule('onboardingLuggage') || hasModule('arrivalLuggage');
  const modes = ALL_MODES.filter((m) => (m.luggage ? handlesLuggage : !m.module || hasModule(m.module)));

  /* Sessions whose attendance window is open right now come first. */
  const sessions = useMemo(() => {
    const all = event?.sessions || [];
    const now = Date.now();
    const live = all.filter((s) => {
      const start = new Date(s.startAt).getTime();
      const end = new Date(s.endAt || s.startAt).getTime();
      const opens = s.attendanceOpensAt ? new Date(s.attendanceOpensAt).getTime() : start - 30 * 60000;
      const closes = s.attendanceClosesAt ? new Date(s.attendanceClosesAt).getTime() : end + 30 * 60000;
      return now >= opens && now <= closes;
    });
    return { live, all: [...all].sort((a, b) => new Date(a.startAt) - new Date(b.startAt)) };
  }, [event]);

  const handleScan = async (code, companions) => {
    if (!eventId) return toast.error('Select an event first');
    const sid = sessionId || sessions.live[0]?._id;
    if (mode === 'session' && !sid) return toast.error('Choose the session you are scanning for');

    setBusy(true);
    try {
      let res;
      if (mode === 'bus_boarding') res = await post('/buses/scan-board', { code, event: eventId });
      else if (mode === 'luggage') res = await post('/luggage/scan', { code });
      else if (mode === 'luggage_tag') res = await post('/luggage/tag-guest', { code, event: eventId });
      else {
        res = await post('/attendance/scan', {
          code, event: eventId, scanType: mode,
          sessionId: mode === 'session' ? sid : undefined,
          gate: mode === 'event_entry' ? 'Main entrance' : undefined,
          ...(companions === undefined ? {} : { companions }),
        });
      }
      if (res.data?.needsParty) {
        const d = res.data;
        setAsking({ code, guest: d.guest, expected: d.expectedCompanions, max: d.maxCompanions, value: String(d.expectedCompanions ?? 0) });
        return;
      }
      setAsking(null);
      const r = toResult(mode, res);
      setResult(r);
      setFeed((f) => [{ ...r, at: new Date(), mode }, ...f].slice(0, 12));
    } catch (err) {
      const r = { tone: 'error', title: 'Not accepted', message: err.message };
      setResult(r);
      setFeed((f) => [{ ...r, at: new Date(), mode }, ...f].slice(0, 12));
    } finally {
      setBusy(false);
    }
  };

  const printTag = (r) => {
    const w = window.open('', '_blank', 'width=420,height=620');
    if (!w) return;
    const safe = (v) => String(v || '').replace(/[<>&"]/g, '');
    w.document.write(`<html><head><title>${safe(r.tagNumber)}</title><style>
      body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;text-align:center;padding:26px;margin:0}
      .tag{border:2px solid #111;border-radius:14px;padding:22px;display:inline-block;min-width:250px}
      .code{font-size:23px;font-weight:800;letter-spacing:2px;margin:14px 0 4px}
      .name{font-size:17px;font-weight:600}.meta{font-size:12px;color:#555;margin-top:6px}
      img{width:210px;height:210px}</style></head>
      <body onload="window.print()"><div class="tag">
        <div class="name">${safe(r.name)}</div><div class="meta">${safe(event?.title)}</div>
        <img src="${r.qrImage}" alt=""/><div class="code">${safe(r.tagNumber)}</div>
        <div class="meta">${safe(r.meta)}</div></div></body></html>`);
    w.document.close();
  };

  if (!eventId) {
    return <EmptyState message="Select an event to start scanning" hint="Your assigned events appear in the picker above." />;
  }

  return (
    <>
      <SectionTitle title="Scan desk" subtitle="Scan passes and bag tags with this device's camera" action={<EventPicker />} />

      <div className="gl-scan-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,0.9fr)', gap: 18 }}>
        <Card padding={20}>
          <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600, marginBottom: 10 }}>
            What are you scanning for?
          </p>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 16 }}>
            {modes.map((m) => (
              <button key={m.value} type="button" onClick={() => { setMode(m.value); setResult(null); }}
                style={{
                  padding: '8px 14px', borderRadius: 99, fontSize: 12.8, fontWeight: 600,
                  border: `1px solid ${mode === m.value ? 'var(--gold-400)' : 'var(--border)'}`,
                  background: mode === m.value ? 'var(--gold-400)' : 'transparent',
                  color: mode === m.value ? 'var(--ink-900)' : 'var(--text-mid)',
                }}>
                {m.label}
              </button>
            ))}
          </div>

          {mode === 'session' && (
            <select value={sessionId} onChange={(e) => setSessionId(e.target.value)}
              style={{
                width: '100%', marginBottom: 16, padding: '10px 12px', borderRadius: 'var(--r)',
                background: 'var(--ink-900)', border: '1px solid var(--border)', color: 'var(--text-hi)', colorScheme: 'dark',
              }}>
              <option value="">{sessions.live.length ? `Live now: ${sessions.live[0].title}` : 'Choose a session…'}</option>
              {sessions.all.map((s) => (
                <option key={s._id} value={s._id}>{s.title} · {fmtTime(s.startAt)}</option>
              ))}
            </select>
          )}

          {asking ? (
            <div style={{ padding: 16, background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)' }}>
              <p style={{ fontSize: 15, fontWeight: 600 }}>{asking.guest?.name}</p>
              <p style={{ fontSize: 12.5, color: 'var(--text-lo)', marginTop: 4, lineHeight: 1.6 }}>
                {Number.isFinite(asking.expected)
                  ? `The list says ${asking.expected} ${asking.expected === 1 ? 'person' : 'people'} with them. Enter who actually came.`
                  : 'The list says nothing about guests. Enter how many came with them.'}
              </p>
              <input
                type="number" min="0" max={asking.max || undefined} autoFocus value={asking.value}
                onChange={(e) => setAsking({ ...asking, value: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') handleScan(asking.code, Number(asking.value) || 0); }}
                style={{
                  width: '100%', margin: '12px 0', padding: '10px 12px', borderRadius: 'var(--r)',
                  background: 'var(--ink-800)', border: '1px solid var(--border)', color: 'var(--text-hi)', fontSize: 15,
                }}
              />
              <div style={{ display: 'flex', gap: 9 }}>
                <Button variant="subtle" onClick={() => setAsking(null)} disabled={busy}>Cancel</Button>
                <Button loading={busy} style={{ flex: 1 }} onClick={() => handleScan(asking.code, Number(asking.value) || 0)}>
                  Mark present
                </Button>
              </div>
            </div>
          ) : (
            <QrScanner
              onScan={handleScan}
              busy={busy}
              hint={mode === 'luggage' ? 'Scan a bag tag, or a guest pass to list their bags'
                : mode === 'luggage_tag' ? 'Scan the guest\'s pass to create a bag tag'
                : 'Scan the attendee\'s pass'}
            />
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div key={`${result.title}-${result.message}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <ResultCard r={result} onNext={() => setResult(null)} onPrint={printTag} />
              </motion.div>
            ) : (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card padding={20}>
                  <p style={{ fontSize: 13, color: 'var(--text-lo)', lineHeight: 1.7 }}>
                    Results appear here. Each scan is checked against {event?.title || 'the selected event'}.
                  </p>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          <Card padding={18}>
            <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600, marginBottom: 10 }}>
              This session
            </p>
            {!feed.length ? (
              <p style={{ fontSize: 12.5, color: 'var(--text-lo)' }}>No scans yet.</p>
            ) : feed.map((f, i) => {
              const t = TONE[f.tone] || TONE.info;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: i ? '1px solid var(--ink-750)' : 'none' }}>
                  <t.Icon size={14} color={t.color} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name || f.title}{f.big ? ` · ${f.big}` : ''}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{fmtTime(f.at)}</span>
                </div>
              );
            })}
          </Card>
        </div>
      </div>

      <style>{`@media (max-width: 960px) { .gl-scan-grid { grid-template-columns: 1fr !important; } }`}</style>
    </>
  );
}

function ResultCard({ r, onNext, onPrint }) {
  const t = TONE[r.tone] || TONE.info;
  return (
    <Card padding={20} style={{ borderColor: t.color }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 'var(--r)', background: t.bg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <t.Icon size={20} color={t.color} />
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: 12, color: t.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>{r.title}</p>
          {r.name && (
            <p style={{ fontSize: 16, fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', gap: 7 }}>
              {r.name} {r.isVip && <Crown size={14} color="var(--gold-400)" />}
            </p>
          )}
          {r.meta && <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 2 }}>{r.meta}</p>}
        </div>
      </div>

      {r.big && <p style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--gold-300)', marginTop: 14 }}>{r.big}</p>}
      <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 8, lineHeight: 1.6 }}>{r.message}</p>
      {r.counts && <Badge tone="neutral" style={{ marginTop: 10 }}>{r.counts}</Badge>}

      {r.qrImage && (
        <div style={{ textAlign: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'inline-block', padding: 10, background: '#fff', borderRadius: 'var(--r)' }}>
            <img src={r.qrImage} alt="Bag tag QR" style={{ width: 150, height: 150, display: 'block' }} />
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 8 }}>Attach this tag to the bag</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 9, marginTop: 16, flexWrap: 'wrap' }}>
        <Button variant="outline" icon={RotateCcw} onClick={onNext}>Scan next</Button>
        {r.qrImage && <Button icon={Printer} onClick={() => onPrint(r)}>Print tag</Button>}
      </div>
    </Card>
  );
}
